/**
 * Phone number display + parse helpers for the PWA.
 *
 * Storage stays E.164 (e.g. `+15551234567`) at the API layer per the
 * User schema's regex — these helpers only translate between E.164
 * and a friendlier display format, never modifying what gets
 * persisted.
 *
 * Display format: NA 10-digit numbers render as `XXX-XXX-XXXX` with
 * the `+1` country code stripped (e.g. `+15551234567` → `555-123-4567`).
 * Anything else — non-NA E.164, malformed strings, in-flight typed
 * input that hasn't reached 10 NA digits yet — passes through
 * unchanged so international users don't see their numbers mangled
 * and so input fields aren't fighting the user's keystrokes mid-type.
 *
 * Two consumers today:
 *   - PhoneCaptureModal: formatNationalAsTyped during input,
 *     parseFromDisplay on submit (composes E.164 from country code
 *     + digits when country === '+1').
 *   - Display surfaces (Profile Account sheet, MyAccessPage owner
 *     contact, MyChargerContactsPage): formatToDisplay on read.
 *     `tel:` / `sms:` href values stay raw E.164 — phones dial off
 *     the unformatted version.
 */

/** Strip every non-digit character. */
function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

/**
 * Format a stored E.164 string for display. NA numbers (+1 + 10
 * digits) render as `XXX-XXX-XXXX`. Anything else returns
 * unchanged so non-NA E.164 ("+447123456789") remains readable.
 *
 * Returns the input as-is when it's null / empty / malformed —
 * callers can use this without an extra null check.
 */
export function formatToDisplay(e164: string | null | undefined): string {
  if (!e164) return '';
  // NA E.164 is exactly +1 followed by 10 digits. Match defensively
  // — anything else short-circuits to the raw value.
  const naMatch = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (naMatch) {
    return `${naMatch[1]}-${naMatch[2]}-${naMatch[3]}`;
  }
  return e164;
}

/**
 * Format the *national-number* portion as the user types in an NA
 * input. Inserts hyphens at positions 3 and 6, strips anything past
 * 10 digits. Backspace works naturally because the function operates
 * on whatever digits the user typed — removing a hyphen-adjacent
 * digit drops the digit and the hyphen disappears on the next render.
 *
 * Used inside PhoneCaptureModal only when the country dropdown is
 * `+1`. For other countries the input stays as-is — international
 * formatting conventions vary and are out of scope for this util.
 */
export function formatNationalAsTyped(raw: string): string {
  const digits = digitsOnly(raw).slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Convert a display-formatted national number back to digits-only
 * for E.164 composition. Trivial wrapper kept for symmetry with
 * formatNationalAsTyped — call sites that already do digits-only
 * stripping can use this instead so the round-trip is explicit.
 */
export function parseFromDisplay(formatted: string): string {
  return digitsOnly(formatted);
}
